import {useCallback, useMemo, useRef, useState} from "react";
import logger from "@/app/logger";
import {toast} from "sonner";
import {IMAGE_VALIDATION, validateImageCount} from "@/lib/validation";

export interface UseFileDragDropOptions {
  /**
   * Current count of files already attached (for count validation)
   */
  currentFileCount: number;
  /**
   * Callback when files are uploaded successfully
   */
  onFilesUploaded: (urls: string[]) => void;
  /**
   * Function to upload multiple files and return their URLs
   */
  uploadMultipleFiles: (files: File[]) => Promise<string[]>;
  /**
   * Whether the component is currently disabled (disables drop)
   */
  disabled?: boolean;
  /**
   * Optional file filter function (e.g., to accept only images)
   * Defaults to accepting all files
   */
  fileFilter?: (file: File) => boolean;
  /**
   * Maximum number of files allowed (for count validation)
   * Defaults to IMAGE_VALIDATION.maxCount
   */
  maxCount?: number;
}

export interface UseFileDragDropReturn {
  /**
   * Whether drag is currently active (for showing drop overlay)
   */
  dragActive: boolean;
  /**
   * Drag event handlers to attach to the drop zone element
   */
  dragHandlers: {
    onDragEnter: (event: React.DragEvent) => void;
    onDragLeave: (event: React.DragEvent) => void;
    onDragOver: (event: React.DragEvent) => void;
    onDrop: (event: React.DragEvent) => Promise<void>;
  };
}

/**
 * Custom hook that encapsulates drag and drop logic for file uploads.
 * Handles drag state management, file validation, and upload coordination.
 *
 * Uses reference counting to prevent flicker when dragging over child elements.
 */
export function useFileDragDrop({
  currentFileCount,
  onFilesUploaded,
  uploadMultipleFiles,
  disabled = false,
  fileFilter,
  maxCount = IMAGE_VALIDATION.maxCount,
}: UseFileDragDropOptions): UseFileDragDropReturn {
  const [dragActive, setDragActive] = useState<boolean>(false);
  const dragCounter = useRef<number>(0);

  /**
   * Handles drag enter events to show drop overlay.
   * Uses reference counting to prevent flicker when dragging over child elements.
   */
  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounter.current++;
      if (event.dataTransfer.types.includes('Files')) {
        setDragActive(true);
      }
    },
    []
  );

  /**
   * Handles drag leave events to hide drop overlay.
   * Only hides when all drag enters have been matched with leaves.
   */
  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  }, []);

  /**
   * Handles drag over events to enable dropping.
   * Must prevent default to allow drop events to fire.
   */
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  /**
   * Handles drop events to process dropped files.
   * Validates, uploads, and updates state.
   */
  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Reset drag state
      if (dragCounter.current !== 1) {
        logger.debug(
          {counterValue: dragCounter.current},
          'Drag counter not at expected value on drop'
        );
      }
      setDragActive(false);
      dragCounter.current = 0;

      // Guard: Don't process if disabled
      if (disabled) {
        return;
      }

      // Extract files from drop event
      const files = Array.from(event.dataTransfer.files);

      // Apply file filter if provided
      const filteredFiles = fileFilter ? files.filter(fileFilter) : files;

      // Validate count
      const {isValid, allowedCount, exceededCount} = validateImageCount(
        currentFileCount,
        filteredFiles.length,
        maxCount
      );

      if (!isValid) {
        logger.warn(
          {
            maxCount,
            attemptedCount: filteredFiles.length,
            exceededCount,
          },
          'File count limit exceeded on drop'
        );
        toast.error(
          `Maximum ${maxCount} files allowed. ${exceededCount} file${
            exceededCount > 1 ? 's' : ''
          } omitted.`
        );
      }

      const filesToUpload = filteredFiles.slice(0, allowedCount);

      if (filesToUpload.length > 0) {
        logger.debug(
          {count: filesToUpload.length, fileNames: filesToUpload.map((f) => f.name)},
          'Files dropped'
        );

        // Upload files
        const uploadedUrls = await uploadMultipleFiles(filesToUpload);

        logger.debug({uploadedCount: uploadedUrls.length}, 'Dropped files uploaded successfully');

        // Update state
        if (uploadedUrls.length > 0) {
          onFilesUploaded(uploadedUrls);
        }
      }
    },
    [disabled, fileFilter, currentFileCount, maxCount, uploadMultipleFiles, onFilesUploaded]
  );

  const dragHandlers = useMemo(() => ({
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
  }), [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return {
    dragActive,
    dragHandlers,
  };
}
